import { ChangeDetectionStrategy, Component, computed, inject, input, model, ViewEncapsulation } from "@angular/core";
import { S25IconButtonComponent } from "../../../standalone/s25.icon.button.component";
import {
    attendanceDisplayName,
    AttendanceService,
    attendanceState,
    AttendanceState,
    attendanceStateName,
    AttendanceStateName,
} from "./attendance.service";
import { SeAttendanceStore } from "./se.attendance.store";
import { S25Util } from "../../../util/s25-util";
import { Attendance } from "./se.attendance.util";

@Component({
    standalone: true,
    selector: "s25-ng-se-own-attendance-state",
    template: `
        @if (registered()) {
            <span class="status-text" [attr.data-state]="attendanceStateName[state()]">{{
                attendanceDisplayName[state()]
            }}</span>
        } @else if (!state()) {
            <s25-ng-icon-button
                class="bookmark-button"
                [type]="'bookmarkOutline'"
                (clicked)="setState('INTERESTED')"
                [ariaLabel]="'Follow'"
            />
        } @else {
            <s25-ng-icon-button
                class="bookmark-button"
                [type]="'bookmarkFilled'"
                [ariaLabel]="'Unfollow'"
                (clicked)="setState('NOT_INTERESTED')"
            />
        }
    `,
    styles: `
        :host {
            display: inline-flex;
        }

        s25-ng-attendance-state {
            font-weight: normal;
            min-width: auto;
        }

        .bookmark-button ::ng-deep .c-iconButton {
            background: color-mix(in srgb, var(--color--surface-bg) 85%, transparent);
            border-radius: 50%;
            padding: 0.55em;
            line-height: 0;
            --icon-color: var(--cn-blue);
            color: var(--cn-blue);
            transition: background var(--transition-short, 80ms) ease;
        }

        .bookmark-button ::ng-deep .c-iconButton:hover {
            background: color-mix(in srgb, var(--color--surface-bg) 92%, transparent);
        }

        .status-text {
            background: var(--color--text-muted);
            color: white;
            padding: 0.3em 0.5em;
            border-radius: 6px;
            font-size: 0.8em;
            white-space: nowrap;

            &[data-state="INTERESTED"] {
                background: var(--cn-blue);
            }

            &[data-state="REGISTERED"] {
                background: var(--color--registration);
            }

            &[data-state="WAIT_LIST"] {
            }

            &[data-state="CHECKED_IN"] {
                background: var(--color--registration);
            }

            &[data-state="NO_SHOW"] {
            }

            &[data-state="CANCELED"] {
            }

            &[data-state="CHECKED_OUT"] {
                background: var(--color--registration);
            }
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    imports: [S25IconButtonComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SeOwnAttendanceStateComponent {
    readonly state = model.required<AttendanceState>();
    readonly eventId = input.required<number>();
    readonly occurrenceId = input.required<number>();

    protected readonly attendanceState = attendanceState;
    readonly attendanceStore = inject(SeAttendanceStore, { optional: true });
    readonly attendanceService = inject(AttendanceService);
    protected readonly attendanceDisplayName = attendanceDisplayName;
    protected readonly attendanceStateName = attendanceStateName;

    readonly registered = computed(() => Attendance.State.isAtLeast(this.state(), attendanceState.PENDING));

    async setState(state: AttendanceStateName) {
        const occurrences = [this.occurrenceId()];
        if (!!this.attendanceStore) {
            await this.attendanceStore.setAttendance(state, { occurrences });
        } else {
            const [_, err] = await S25Util.Maybe(
                this.attendanceService.updateAttendance(this.eventId(), state, { occurrences }),
            );
            if (!!err) {
                S25Util.showError(err);
                return false;
            }
        }

        this.state.set(attendanceState[state]);
    }
}
